import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import StructureImage from './StructureImage';
import ExternalLinkWarning, { openExternalLink } from '../common/ExternalLinkWarning';
import { ZONE_COLORS, ZONE_LABELS } from '../../utils/patentZones';

interface CompoundDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  surechemblId: string;
  smiles?: string;
  similarity?: number;
  zone?: number;
  patentNumber?: string;
  matchType?: string;
}

const CompoundDetailModal: React.FC<CompoundDetailModalProps> = ({
  isOpen,
  onClose,
  surechemblId,
  smiles,
  similarity,
  zone,
  patentNumber,
  matchType,
}) => {
  const [copied, setCopied] = useState(false);
  const [warning, setWarning] = useState({ url: '', isOpen: false });

  if (!isOpen) return null;

  const normalizedId = surechemblId.startsWith('SCHEMBL')
    ? surechemblId
    : `SCHEMBL${surechemblId}`;

  const surechemblUrl = `https://www.surechembl.org/chemical/${normalizedId}`;

  const handleCopySmiles = () => {
    if (!smiles) return;
    navigator.clipboard.writeText(smiles).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleExternalLink = () => {
    openExternalLink(surechemblUrl, setWarning);
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ zIndex: 100000, backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={onClose}
      >
        <div
          className="rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto"
          style={{ backgroundColor: 'var(--card-bg, #1e1e2e)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b"
            style={{ borderColor: 'var(--card-border)' }}>
            <div>
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
                {normalizedId}
              </h3>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Compound Detail</p>
            </div>
            <button onClick={onClose} className="p-1 rounded transition-colors hover:opacity-80"
              style={{ color: 'var(--text-muted)' }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-4">
            {/* Structure image */}
            {smiles && (
              <div className="flex justify-center p-4 rounded-lg"
                style={{ backgroundColor: 'var(--content-bg, #141420)', border: '1px solid var(--card-border)' }}>
                <StructureImage smiles={smiles} size={240} />
              </div>
            )}

            {/* Similarity + Zone badges */}
            <div className="flex items-center gap-3 flex-wrap">
              {similarity != null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Similarity:</span>
                  <span className="px-2 py-0.5 rounded text-xs font-semibold text-white"
                    style={{ backgroundColor: similarity >= 0.95 ? '#ef4444' : similarity >= 0.85 ? '#f97316' : similarity >= 0.75 ? '#eab308' : '#3b82f6' }}>
                    {(similarity * 100).toFixed(1)}%
                  </span>
                </div>
              )}
              {zone != null && zone >= 1 && zone <= 5 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Zone:</span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                    style={{ backgroundColor: `${ZONE_COLORS[zone]}20`, color: ZONE_COLORS[zone], border: `1px solid ${ZONE_COLORS[zone]}40` }}>
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ZONE_COLORS[zone] }} />
                    {ZONE_LABELS[zone]}
                  </span>
                </div>
              )}
              {matchType && (
                <span className="text-xs px-2 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--hover-bg)', color: 'var(--text-muted)', border: '1px solid var(--card-border)' }}>
                  {matchType}
                </span>
              )}
            </div>

            {/* Patent reference */}
            {patentNumber && (
              <div>
                <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Patent: </span>
                <span className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>{patentNumber}</span>
              </div>
            )}

            {/* SMILES */}
            {smiles && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>SMILES</span>
                  <button
                    onClick={handleCopySmiles}
                    className="text-xs px-2 py-0.5 rounded transition-colors"
                    style={{
                      color: copied ? '#22c55e' : '#0d9488',
                      backgroundColor: copied ? 'rgba(34,197,94,0.1)' : 'rgba(13,148,136,0.1)',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div className="px-3 py-2 rounded-lg overflow-x-auto"
                  style={{ backgroundColor: 'var(--content-bg, #141420)', border: '1px solid var(--card-border)' }}>
                  <code className="text-xs break-all" style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {smiles}
                  </code>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t"
            style={{ borderColor: 'var(--card-border)' }}>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg transition-colors"
              style={{ color: 'var(--text-secondary)', border: '1px solid var(--card-border)', backgroundColor: 'transparent' }}
            >
              Close
            </button>
            <button
              onClick={handleExternalLink}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#0d9488' }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#0f766e'; }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#0d9488'; }}
            >
              View on SureChEMBL &#8599;
            </button>
          </div>
        </div>
      </div>

      <ExternalLinkWarning
        url={warning.url}
        isOpen={warning.isOpen}
        onClose={() => setWarning(w => ({ ...w, isOpen: false }))}
        onConfirm={() => setWarning(w => ({ ...w, isOpen: false }))}
      />
    </>,
    document.body,
  );
};

export default CompoundDetailModal;
